import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {User} from '../../../../types/user.class';
import {TaskService} from '../../../../services/task.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GenBulkUploadRequest} from '../../../../generated/serverModels/GenBulkUploadRequest';
import {appFailure} from '../../../../services/error-handling';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Task} from '../../../../types/task.class';
import {Project} from '../../../../types/project.class';
import {DocumentTagService} from '../../../../services/document-tag.service';
import {UntypedFormControl} from '@angular/forms';
import {catchError, delay, map, mergeMap, startWith, tap} from 'rxjs/operators';
import {UserService} from '../../../../services/user.service';
import {from, Observable, of} from 'rxjs';
import {GenUser} from '../../../../generated/serverModels/GenUser';
import {GenWorkflowStatus} from '../../../../generated/serverModels/GenWorkflowStatus';

@Component({
  selector: 'portal-bulk-upload-dialog',
  templateUrl: './bulk-upload-dialog.component.html',
  styleUrl: './bulk-upload-dialog.component.scss'
})
export class BulkUploadDialogComponent {

  request = new BulkUploadRequest();
  displayedColumns = ['name', 'filename', 'tags', 'status'];
  tags: string[] = [];
  @ViewChild('fileInput', {static: true}) fileInput: ElementRef;
  contributorControl: UntypedFormControl;
  filteredContributors: Observable<User[]>;
  users: User[];
  uploading = false;
  haveUploaded = false;
  files: File[] = [];

  constructor(
    private taskService: TaskService,
    private documentTagService: DocumentTagService,
    private userService: UserService,
    private popup: MatSnackBar,
    public ref: MatDialogRef<BulkUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BulkUploadDialogConfig,
  ) {
    this.contributorControl = new UntypedFormControl();
    this.documentTagService.getAll()
      .subscribe((array) => {
        this.tags = array.sort((a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : a.toLowerCase() > b.toLowerCase() ? 1 : 0);
      });

    this.userService.getContributors().subscribe((users) => {
      this.users = users.sort((user1, user2) => {
        return (user1.name.toLowerCase() < user2.name.toLowerCase()) ? -1 : (user1.name.toLowerCase() > user2.name.toLowerCase()) ? 1 : 0;
      }).filter((user) => {
        return user.isContributor();
      });

      if (this.contributorControl.value) {
        this.filteredContributors = this.contributorControl.valueChanges.pipe(
          map(name => {
            return this.filterUsers(name);
          }));
      } else {
        this.filteredContributors = this.contributorControl.valueChanges.pipe(
          startWith(undefined),
          map(name => {
            return this.filterUsers(name);
          }));
      }
    });

  }

  selectContributor(user: User) {
    user.roles = []; // just so the server side can deserialize it.
    this.request.contributor = user;
  }

  displayUserInfo(user: User) {
    return (!user) ? '' : `${user.name}, ${user.firstName} ${user.lastName}, ${user.email}`;
  }

  onFilesPicked(files: FileList) {
    const tasks = Array.from(files).map(file => {
      const t = new Task();
      t.originalFilename = file.name;
      t.projectId = this.data.project.id;
      return t;
    });
    tasks.forEach((task) => {
      this.request.itemsToPrepareForUpload.push(task);
      const file: any = Array.from(this.fileInput.nativeElement.files)
        .find((f: File) => f.name === task.originalFilename);
      this.files.push(file);
    });
    this.taskService.parseBulkUpload(this.request)
      .subscribe({
        next: req => {
          this.popup.open('Files selected for upload', 'OK', {duration: 5000});
          this.request.itemsToPrepareForUpload = req.itemsToPrepareForUpload;
        },
        error: appFailure(this.popup, 'Error parsing filenames')
      });
  }

  public filterUsers(val: string) {
    if (!val || !val.toLowerCase) {
      return this.users;
    }
    val = val.toLowerCase().trim();
    const searchArr = val.split(' ');
    return this.users.filter(user => {
      const haystack = [
        user.firstName,
        user.lastName,
        user.email,
        user.name
      ]
        .concat(user.roles.map(role => role.name))
        .map(str => str.toLowerCase())
        .join();
      return ((!this.data.project.members || this.data.project.members.indexOf(user) === -1) && searchArr.every((val) => haystack.indexOf(val) !== -1));
    });
  }

  concatName(user: GenUser) {
    return (user) ? `${user.firstName} ${user.lastName}` : ``;
  }

  readyForFiles() {
    return this.request.namePrefix && this.request.contributor;
  }

  chooseFiles() {
    this.fileInput.nativeElement.click();
  }

  uploadAndSave() {
    this.uploading = true;
    this.taskService
      .startBulkUpload(this.request)
      .subscribe({
        next: response => {
          this.request.applyUpdatesFromServer(response);

          from(this.request.itemsToPrepareForUpload).pipe(
            mergeMap(task => {
              const file = Array
                .from(this.fileInput.nativeElement.files)
                .find((f: File) => f.name === task.originalFilename);
              task.uploadProgress = 0;
              task.status = GenWorkflowStatus.Complete;

              return this.taskService.save(task, file).pipe(
                tap((event: XMLHttpRequest | ProgressEvent) => {
                  if (event instanceof ProgressEvent && event.lengthComputable) {
                    if (event.lengthComputable && event.total > 0) {
                      task.uploadProgress = Math.round((event.loaded / event.total) * 100);
                    } else {
                      task.uploadProgress = undefined;
                    }
                  } else {
                    task.status = GenWorkflowStatus.Complete;
                    task.uploadProgress = 100;
                  }
                }),
                catchError(err => {
                  task.status = GenWorkflowStatus.Error;
                  task.bulkUploadErrorMessage = err?.message ?? 'Upload failed';
                  task.uploadProgress = undefined;
                  return of(null);
                })
              );
            }, 2) // <= only 2 uploads in-flight at once
          ).subscribe({
            complete: () => {
              const anyFailures = this.request.itemsToPrepareForUpload.some(t => t.status === GenWorkflowStatus.Error);
              this.uploading = false;
              this.haveUploaded = true;
              if (anyFailures) {
                this.popup.open('Finished with some errors.');
              } else {
                this.popup.open('All Done!');
              }
            }
          });
        },
        error: appFailure(this.popup, 'Starting Bulk Upload', () => this.uploading = false)
      });

  }

}

export interface BulkUploadDialogConfig {
  user: User;
  project: Project;
}

export class BulkUploadRequest extends GenBulkUploadRequest {

  override itemsToPrepareForUpload: Task[] = [];

  constructor(input?: any) {
    super(input);
    if (!this.itemsToPrepareForUpload) {
      this.itemsToPrepareForUpload = [];
    }
    if (!this.namePrefix) {
      this.namePrefix = '';
    }
    if (input?.itemsToPrepareForUpload) {
      this.itemsToPrepareForUpload = input.itemsToPrepareForUpload.map(t => new Task(t));
    }
  }

  applyUpdatesFromServer(fromServer: BulkUploadRequest) {
    for (let i = 0; i < fromServer.itemsToPrepareForUpload.length; i++) {
      if (!this.itemsToPrepareForUpload[i]) {
        this.itemsToPrepareForUpload[i] = fromServer.itemsToPrepareForUpload[i];
      } else {
        Object.assign(this.itemsToPrepareForUpload[i], fromServer.itemsToPrepareForUpload[i]);
      }
    }
  }
}
